import { Input } from '@components/ui/input';
import React, { useEffect, useState } from 'react';

type CurrencyValue<T = string> = {
  amount: number;
  currency: T;
};

type CurrencyInputProps<T = string> = {
  allowLeadingZeros?: boolean;
  className?: string;
  currency?: T;
  currencyPosition?: 'left' | 'right';
  disabled?: boolean;
  emptyValue?: 'undefined' | 'zero';
  maxDecimals?: number;
  onChange: (value: CurrencyValue<T> | undefined) => void;
  placeholder?: string;
  showCurrencyCode?: boolean;
  showCurrencySymbol?: boolean;
  value?: CurrencyValue<T>;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'>;

const formatNumberWithCommas = (value: string | number | undefined | null): string => {
  if (value === undefined || value === null || value === '') return '';
  const numberStr = value.toString().replace(/,/g, '');
  if (numberStr.endsWith('.')) {
    return numberStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  if (numberStr.includes('.')) {
    const parts = numberStr.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }
  return numberStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    AUD: 'A$',
    CAD: 'C$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    USD: '$',
  };
  return symbols[currency] || '$';
};

export const CurrencyInput = <T = string>(props: CurrencyInputProps<T>) => {
  const {
    allowLeadingZeros = false,
    className = '',
    currency = 'USD' as T,
    currencyPosition = 'left',
    disabled = false,
    emptyValue = 'zero',
    maxDecimals = 2,
    onChange,
    placeholder = '0.00',
    showCurrencyCode = true,
    showCurrencySymbol = true,
    value,
  } = props;

  const effectiveCurrency = value?.currency ?? currency;

  const [displayValue, setDisplayValue] = useState(() => {
    return value?.amount !== undefined ? value.amount.toString() : '';
  });

  // Update display value when form value changes from outside (e.g., reset)
  useEffect(() => {
    const newDisplayValue = value?.amount ? value.amount.toString() : '';
    setDisplayValue(newDisplayValue);
  }, [value?.amount]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/,/g, '').trim();

    if (rawValue === '') {
      setDisplayValue('');
      if (emptyValue === 'undefined') {
        onChange(undefined);
      } else {
        onChange({
          amount: 0,
          currency: effectiveCurrency,
        });
      }
      return;
    }

    // Validate input format
    if (!/^\d*\.?\d*$/.test(rawValue)) return;
    const parts = rawValue.split('.');
    if (parts.length > 2) return;
    if (parts.length === 2 && parts[1].length > maxDecimals) return; // Limit decimal places
    if (!allowLeadingZeros && rawValue.length > 1 && rawValue.startsWith('0') && !rawValue.startsWith('0.')) return; // Prevent leading zeros like "01"

    const numValue = parseFloat(rawValue);

    // Update display string with the raw input (preserves user typing)
    setDisplayValue(rawValue);

    // Update form data with parsed number
    if (!isNaN(numValue)) {
      onChange({
        amount: numValue,
        currency: effectiveCurrency,
      });
    }
  };

  const currencySymbol = getCurrencySymbol(String(effectiveCurrency));

  let leftContent = '';
  let rightContent = '';

  if (showCurrencyCode) {
    if (currencyPosition === 'left') {
      leftContent = String(effectiveCurrency);
    } else {
      rightContent = String(effectiveCurrency);
    }
  } else if (showCurrencySymbol) {
    if (currencyPosition === 'left') {
      leftContent = currencySymbol;
    } else {
      rightContent = currencySymbol;
    }
  }

  // Calculate padding based on content
  const leftPadding = leftContent ? 'pl-6' : '';
  const rightPadding = rightContent ? 'pr-12' : '';

  return (
    <div className="relative w-full">
      {leftContent && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
          {leftContent}
        </span>
      )}
      <Input
        className={`text-right ${leftPadding} ${rightPadding} ${className}`}
        disabled={disabled}
        inputMode="decimal"
        onChange={handleInputChange}
        placeholder={placeholder}
        type="text"
        value={formatNumberWithCommas(displayValue)}
      />
      {rightContent && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
          {rightContent}
        </span>
      )}
    </div>
  );
};
